import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  createFilterOptions,
} from "@mui/material";
import AutoCompleteTagInput from "../../components/AutoCompleteTagInput/AutoCompleteTagInput";
import Loader from "../../components/Loader/Loader";
import dayjs from "dayjs";

const AssociateModule = ({
  open,
  onClose,
  bootcampDetails,
  getBootcampData,
  handleAssociateModule,
  moduleList,
  loader,
  associateModuleApiError,
}) => {
  const initialErrorState = {
    moduleIds: "",
  };

  const [errors, setErrors] = useState(initialErrorState);
  const [moduleIds, setModuleIds] = useState([]);
  const [defaultModuleIds, setDefaultModuleIds] = useState([]);

  useEffect(() => {
    if (open && bootcampDetails) {
      // Get existing modules for this bootcamp
      const existingModules =
        getBootcampData?.getCreateEditBootcampOptions?.data?.modules?.results?.filter(
          (module) =>
            getBootcampData?.getCreateEditBootcampOptions?.data?.selectedModuleIds?.results?.includes(
              module?.id
            )
        ) || [];
      setModuleIds(existingModules);
      // Store default module IDs (non-removable)
      setDefaultModuleIds(existingModules?.map((module) => module?.id) || []);
    } else if (!open) {
      setModuleIds([]);
      setDefaultModuleIds([]);
    }
  }, [open, bootcampDetails, getBootcampData]);

  const validateFields = () => {
    const newErrors = {
      moduleIds:
        moduleIds && moduleIds.length > 0 ? "" : "Modules are required",
    };
    setErrors(newErrors);
    return Object.values(newErrors).every((error) => error === "");
  };

  const handleOnClose = () => {
    setErrors(initialErrorState);
    setModuleIds([]);
    onClose();
  };

  const handleChipDelete = (optionToDelete) => {
    // Prevent deletion of default modules
    if (defaultModuleIds?.includes(optionToDelete?.id)) {
      return;
    }
    const newModuleIds = moduleIds?.filter(
      (option) => option?.id !== optionToDelete?.id
    );
    setModuleIds(newModuleIds);
    setErrors((prev) => {
      return {
        ...prev,
        moduleIds: "",
      };
    });
  };

  const handleSubmit = () => {
    if (validateFields()) {
      handleAssociateModule({
        bootcampId: bootcampDetails?.id,
        moduleIds: moduleIds?.map((module) => module?.id) || [],
      });
    }
  };

  return (
    <>
      {loader && <Loader />}
      <Dialog
        className="modal-drawer-container"
        open={open}
        onClose={handleOnClose}
        fullWidth
        PaperProps={{
          style: {
            borderRadius: "1rem",
            boxShadow: "0rem 0.6rem 3rem 0rem #OD",
            zIndex: 1300,
            maxWidth: "29.75rem",
            height: "auto",
          },
        }}
      >
        <DialogTitle className="dialog-title">Associate Module</DialogTitle>
        <DialogContent className="dialog-content">
          <Box className="width-100" sx={{ marginTop: "1rem" }}>
            <AutoCompleteTagInput
              label="Modules"
              placeholder={moduleIds?.length ? null : "Select Module"}
              options={moduleList}
              selectedValue={moduleIds}
              disabled={!bootcampDetails?.startDate}
              getOptionLabel={(option) => option?.name}
              filterOptions={(options, params) => {
                const filter = createFilterOptions();
                const filtered = filter(options, params);
                if (moduleList?.length === 0) {
                  return [{ name: "No options", all: false }];
                }

                return [
                  {
                    name: "Select All",
                    all: true,
                  },
                  ...filtered,
                ];
              }}
              onChange={(event, newValue) => {
                const idCounts = newValue.reduce((counts, { id }) => {
                  counts[id] = (counts[id] || 0) + 1;
                  return counts;
                }, {});
                const uniqueNewValue = newValue.filter(({ id }) => {
                  return idCounts[id] === 1;
                });
                if (newValue.find((option) => option.all)) {
                  // When selecting all, ensure default modules are always included
                  const allModules =
                    moduleIds?.length === moduleList?.length ? [] : moduleList;
                  // Always include default modules
                  const defaultModules =
                    moduleList?.filter((module) =>
                      defaultModuleIds?.includes(module?.id)
                    ) || [];
                  const newModules =
                    allModules?.filter(
                      (module) => !defaultModuleIds?.includes(module?.id)
                    ) || [];
                  return setModuleIds([...defaultModules, ...newModules]);
                }
                // Ensure default modules are always included
                const defaultModules =
                  moduleIds?.filter((module) =>
                    defaultModuleIds?.includes(module?.id)
                  ) || [];
                const newSelectedModules =
                  uniqueNewValue?.filter(
                    (module) => !defaultModuleIds?.includes(module?.id)
                  ) || [];
                setModuleIds([...defaultModules, ...newSelectedModules]);
                setErrors((prev) => {
                  return {
                    ...prev,
                    moduleIds: "",
                  };
                });
              }}
              renderOption={(props, option, value) => {
                if (option.name === "No options") {
                  return (
                    <Box className="auto-complete-no-options">
                      <Box>{option?.name}</Box>
                    </Box>
                  );
                }

                const optionChecked =
                  (option.all && moduleIds?.length === moduleList?.length) ||
                  moduleIds?.find((e) => e?.id === option?.id) != null;
                const isDefaultModule = defaultModuleIds?.includes(option?.id);

                return (
                  <li
                    {...props}
                    key={props?.id}
                    className={
                      optionChecked
                        ? "multi-tag-edit-list-selected"
                        : "multi-tag-style"
                    }
                  >
                    <Checkbox
                      className="auto-complete-checkbox-list"
                      size="small"
                      checked={optionChecked}
                      color="secondary"
                      disabled={isDefaultModule}
                    />
                    <Box className="flex-options">
                      <Box>{option?.name}</Box>
                      {option?.name === "Select All" ? null : (
                        <Box>
                          {dayjs(option?.startDate)?.format("DD/MM/YYYY")}
                          {option?.endDate && (
                            <>
                              {" - "}
                              {dayjs(option?.endDate)?.format("DD/MM/YYYY")}
                            </>
                          )}
                        </Box>
                      )}
                    </Box>
                  </li>
                );
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const isDefaultModule = defaultModuleIds?.includes(
                    option?.id
                  );
                  return (
                    <Chip
                      {...getTagProps(index)}
                      key={index}
                      label={option?.name}
                      onDelete={
                        isDefaultModule
                          ? undefined
                          : () => handleChipDelete(option)
                      }
                    />
                  );
                })
              }
              getOptionDisabled={(option) =>
                defaultModuleIds?.includes(option?.id)
              }
              error={!!errors.moduleIds || associateModuleApiError}
              helperText={errors.moduleIds}
              required={true}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: "0rem 2rem 1.875rem" }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleOnClose}
            className="block-button"
          >
            <Typography variant="outlineBtnLabel">Cancel</Typography>
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="secondary"
            className="block-button"
          >
            <Typography variant="outlineBtnLabel">Associate</Typography>
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AssociateModule;
